import { FC, useMemo } from 'react';

import lang from '../../../lib/language';

import GoBackButton from '../../../components/GoBackButton/GoBackButton';
import Grid from '../../../components/Grid/Grid';
import NavButtons from '../../../components/NavigationButtons/NavButtons';
import LatestNews from '../../../components/News/LatestNews/LatestNews';
import Preloader from '../../../components/Preloader/Preloader';
import SymbolTable from '../../../components/SymbolTable/SymbolTable';
import { SymbolTableGrid } from '../../../components/SymbolTable/SymbolTableGrid.enum';
import useIsMobile from '../../hooks/useIsMobile';
import PopularGroupTableItem from '../../PopularGroups/components/PopularGroupTableItem/PopularGroupTableItem';
import { SYMBOL_TABLE_HEADERS } from '../../PopularGroups/PopularGroups.constants';
import { PopularGroupItemRow } from '../../PopularGroups/PopularGroups.types';

import { HotGappersInternalProps } from './HotGappersInternal.types';

import styles from './HotGappersInternal.module.scss';

const PRELOADER_STYLE = { height: '36px', width: '36px', margin: '0 auto' };
const GAP_TRACKER_TAB_STATE_KEY = 'gapTrackerTab';

const HotGappersInternal: FC<HotGappersInternalProps> = ({
  rows,
  activeTabIndex,
  isPending,
  isLoading,
  hasMorePages,
  showLoadingSpinner,
  enableRowToggle,
  titleRef,
  onTabChange,
  onLoadMore,
  onSymbolClick,
  onGoBack,
}: HotGappersInternalProps): JSX.Element => {
  const { isMobile } = useIsMobile();

  const tableHeaders = useMemo(() => (
    <div className={styles.tableHeaderRow}>
      {SYMBOL_TABLE_HEADERS.map((item: string) => (
        <div key={item} className={styles.tableHeaderCell}>
          <span>{item}</span>
        </div>
      ))}
    </div>
  ), []);

  const tabs = [
    { text: lang.gappersTitleGapUp(), component: <></> },
    { text: lang.gappersTitleGapDown(), component: <></> },
  ];

  const extraChildren = () => !isMobile && <div className={styles.wrapperSecondSection}><LatestNews /></div>;

  return (
    <Grid extraChildren={extraChildren()}>
      <div className={styles.wrapper}>
        <div>
          {!isMobile && (
            <div className={styles.goBackButton}>
              <GoBackButton onBackBtnPress={onGoBack} />
            </div>
          )}
          <div className={styles.gappersTitleWrapper}>
            <div ref={titleRef} className={isMobile ? styles.titleMobile : styles.title}>
              {lang.gappersTitle()}
            </div>
            <div className={isMobile ? styles.stocksLabelMobile : styles.stocksLabel}>
              {lang.gapperInfo()}
            </div>
          </div>
          <div className={styles.gappersWrapper}>
            <NavButtons
              tabs={tabs}
              sendCurrentTabIndex={onTabChange}
              initialActiveTab={activeTabIndex}
              persistTabState={GAP_TRACKER_TAB_STATE_KEY}
            />
            <div className={styles.gappersTable}>
              <SymbolTable
                items={rows}
                renderHeaders={() => tableHeaders}
                skeletonCount={rows.length}
                skeletonGrid={SymbolTableGrid.FIVE_ROW_ITEMS_GRID}
                isLoading={isLoading}
                renderRow={(row: PopularGroupItemRow) => (
                  <PopularGroupTableItem
                    key={row.item.symbol}
                    row={row}
                    enableToggle={enableRowToggle}
                    onSymbolClick={onSymbolClick}
                  />
                )}
              />
              {hasMorePages && (
                <div className={styles.viewMore}>
                  <button
                    type="button"
                    onClick={onLoadMore}
                    className={styles.viewMoreButton}
                    disabled={isPending}
                  >
                    {lang.commonLoadMore()}
                  </button>
                </div>
              )}
              {isPending && showLoadingSpinner && <Preloader style={PRELOADER_STYLE} text="" />}
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default HotGappersInternal;
